import { BaseApiService } from '../base/BaseApiService';
import { ApiResponse } from '../../types/api';
import type { SuspensionPeriodMode } from '../../types/suspensions';

// DTOs para linhas de suspensão
export interface SuspensionLineDto {
  id: string;
  numero: string | null;
  ddd: string;
  planName: string;
  contractId: string;
  contractNumber: string;
  clientId: string;
  clientName: string;
  clientCnpj: string;
  contractStatus: string;
  contractStatusEnum: string;
  fastchip: boolean;
  fastchipEnviadoEm: string | null;
  eligibleForUrgencyMetrics: boolean;
  dataSuspensao: string | null;
  dataProximaSuspensao: string | null;
  diasRestantes: number | null;
  estaSuspensa: boolean;
  temCredenciais: boolean;
  usuarioQueMarcouSuspensao: string | null;
  createdBy: string;
  teamName: string | null;
  contractCreatedAt: string;
  contractApprovedAt?: string | null;
  protocolo: string | null;
  trocaLinha: boolean;
  motivoOperacional?: string | null;
  motivoOperacionalLabel?: string | null;
  dataPrevistaSuspensao?: string | null;
  automationStatus?: string | null;
  automationOutcome?: string | null;
  automationFailureReason?: string | null;
  automationAttempts?: number;
  automationQueuedAt?: string | null;
  automationLastProcessedAt?: string | null;
  manualReviewRequired?: boolean;
  manualReviewReason?: string | null;
  /** Presente em algumas respostas do dashboard (Fastchip / ICCID). */
  chipCcid?: string | null;
}

export interface MarkSuspendedBatchOptions {
  lineIds: string[];
  suspensionDate?: string;
  motivoOperacional?: string;
  dataPrevistaSuspensao?: string;
  /** Protocolo TIM único para todas as linhas do lote */
  protocolo?: string;
  /** Protocolo por linha (re-suspensões com protocolos distintos) */
  lineProtocols?: Array<{ lineId: string; protocolo: string }>;
}

export interface SuspensionProtocolRecordDto {
  id: string;
  contractLineId: string;
  ddd: string;
  numero: string | null;
  protocolo: string;
  dataSuspensaoEfetiva: string | null;
  source: string;
  createdAt: string;
  createdByName: string | null;
}

export interface ContractSuspensionProtocolsResponse {
  contractId: string;
  items: SuspensionProtocolRecordDto[];
}

export interface UpdateSuspensionTraceDto {
  motivoOperacional?: string;
  dataPrevistaSuspensao?: string;
  clearMotivo?: boolean;
  clearDataPrevista?: boolean;
}

export interface SuspensionSummaryDto {
  contract: {
    id: string;
    contractNumber: string;
    status: string;
    valorTotalMensal: number;
    dataCriacao: string;
  };
  client: {
    id: string;
    razaoSocial: string;
    cnpj: string;
    nomeFantasia: string;
    email: string;
    telefone1: string;
  };
  suspensionLines: Array<{
    id: string;
    ddd: string;
    numero: string | null;
    planName: string;
    dataProximaSuspensao: string | null;
    diasRestantes: number | null;
    protocolo: string | null;
  }>;
  credentials: {
    meuTimLogin: string | null;
    meuTimSenha: string | null;
    temCredenciais: boolean;
  } | null;
  deliveryAddress: {
    cep: string;
    endereco: string;
    numero: string;
    complemento: string | null;
    bairro: string;
    cidade: string;
    estado: string;
    referencia: string | null;
  } | null;
}

export interface SuspensionStatsDto {
  totalPending: number;
  withImpedimento?: number;
  firstSuspension: number;
  urgent: number;
  alert: number;
  normal: number;
  suspendedToday: number;
  suspendedThisWeek: number;
  suspendedThisMonth: number;
  suspendedInPeriod: number;
  missingCredentials: number;
  chartData: Array<{
    period: string;
    periodLabel: string;
    suspended: number;
    pending: number;
  }>;
  byUrgency: {
    firstSuspension: number;
    urgent: number;
    alert: number;
    normal: number;
    withImpedimento?: number;
    noDate: number;
  };
  topClients: Array<{
    clientId: string;
    clientName: string;
    clientCnpj: string;
    pendingCount: number;
  }>;
}

export interface UpdateCredentialsDto {
  meuTimLogin: string;
  meuTimSenha: string;
  applyToContract?: boolean;
}

export interface BatchSuspensionResult {
  requested: number;
  updated: number;
  notFound: number;
  alreadySuspended: number;
  notPending: number;
  updatedLineIds: string[];
}

export interface LineCredentialsDto {
  id: string;
  meuTimLogin: string | null;
  meuTimSenha: string | null;
  contractId: string;
}

/**
 * Fatia 1 (GCM) — elegibilidade real da ação de 1ª suspensão para uma linha, vinda do
 * servidor (`GcmFirstSuspensionEligibilityDto`). GCM nunca vê/aciona re-suspensão — só a 1ª.
 */
export interface GcmFirstSuspensionEligibilityDto {
  lineId: string;
  contractId: string;
  eligible: boolean;
  alreadySuspended: boolean;
  contractStatus: string | null;
  isTeamMember: boolean;
  healthOk: boolean;
  reason: string | null;
}

export interface GcmFirstSuspensionActionResultDto {
  lineId: string;
  success: boolean;
  message: string;
  /** "manual" | "automacao" */
  origin: string;
}

export interface GcmMarkFirstSuspensionRequestDto {
  protocolo?: string;
  suspensionDate?: string;
}

export interface PendingToSendLineDto {
  id: string;
  numero: string | null;
  ddd: string;
  planName: string;
  contractId: string;
  contractNumber: string;
  clientName: string;
  clientCnpj: string;
  negociacao: string;
  dataCriacao: string;
  contractCreatedAt: string;
  contractApprovedAt?: string | null;
  contractStatus: string;
  createdBy: string;
  teamName: string | null;
  chipCcid: string | null;
  contractValorTotalMensal: number | null;
  fastchip: boolean;
  deliveryAddress: {
    cep: string;
    endereco: string;
    numero: string;
    complemento: string | null;
    bairro: string;
    cidade: string;
    estado: string;
    referencia: string | null;
  } | null;
}

class ContractLineService extends BaseApiService {
  /**
   * Obtém lista de linhas pendentes de suspensão
   */
  async getPendingSuspensions(filters?: {
    userId?: string;
    status?: string;
    searchTerm?: string;
    contractStatus?: string;
    fastchipEnviado?: boolean;
    temCredenciaisMeuTim?: boolean;
    contractId?: string;
  }): Promise<SuspensionLineDto[]> {
    const params = new URLSearchParams();
    
    if (filters?.userId) {
      params.append('userId', filters.userId);
    }
    if (filters?.contractId) {
      params.append('contractId', filters.contractId);
    }
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.searchTerm) {
      params.append('searchTerm', filters.searchTerm);
    }
    if (filters?.contractStatus) {
      params.append('contractStatus', filters.contractStatus);
    }
    if (filters?.fastchipEnviado !== undefined) {
      params.append('fastchipEnviado', String(filters.fastchipEnviado));
    }
    if (filters?.temCredenciaisMeuTim !== undefined) {
      params.append('temCredenciaisMeuTim', String(filters.temCredenciaisMeuTim));
    }

    const queryString = params.toString();
    const url = `/v1/contract-lines/pending-suspensions${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.api.get<SuspensionLineDto[]>(url);
    return this.extractData(response);
  }

  /**
   * Marca uma linha como suspensa
   */
  async markAsSuspended(lineId: string): Promise<{ success: boolean; message: string; data: any }> {
    const response = await this.api.put<ApiResponse<{ success: boolean; message: string; data: any }>>(
      `/v1/contract-lines/${lineId}/mark-suspended`,
      {}
    );
    return this.extractData(response);
  }

  async markAsSuspendedBatch(options: MarkSuspendedBatchOptions | string[]): Promise<BatchSuspensionResult> {
    const payload = Array.isArray(options)
      ? { lineIds: options }
      : {
          lineIds: options.lineIds,
          suspensionDate: options.suspensionDate,
          motivoOperacional: options.motivoOperacional,
          dataPrevistaSuspensao: options.dataPrevistaSuspensao,
          protocolo: options.protocolo,
          lineProtocols: options.lineProtocols,
        };
    const response = await this.api.put<ApiResponse<BatchSuspensionResult>>(
      '/v1/contract-lines/mark-suspended-batch',
      payload
    );
    return this.extractData(response);
  }

  async updateSuspensionTrace(lineId: string, body: UpdateSuspensionTraceDto): Promise<void> {
    await this.api.patch(`/v1/contract-lines/${lineId}/suspension-trace`, body);
  }

  async getSuspensionProtocolsByContract(contractId: string): Promise<ContractSuspensionProtocolsResponse> {
    const response = await this.api.get<ContractSuspensionProtocolsResponse>(
      `/v1/contract-lines/suspension-protocols/by-contract/${contractId}`
    );
    return this.extractData(response);
  }

  async addSuspensionProtocol(
    lineId: string,
    body: { protocolo: string; dataSuspensaoEfetiva?: string }
  ): Promise<SuspensionProtocolRecordDto> {
    const response = await this.api.post<SuspensionProtocolRecordDto>(
      `/v1/contract-lines/${lineId}/suspension-protocols`,
      body
    );
    return this.extractData(response);
  }

  async deleteSuspensionProtocol(protocolId: string): Promise<void> {
    await this.api.delete(`/v1/contract-lines/suspension-protocols/${protocolId}`);
  }

  async cancelSuspensionBatch(lineIds: string[]): Promise<BatchSuspensionResult> {
    const response = await this.api.put<ApiResponse<BatchSuspensionResult>>(
      '/v1/contract-lines/cancel-suspension-batch',
      { lineIds }
    );
    return this.extractData(response);
  }

  /**
   * Reverte a marcação de suspensão de uma linha
   */
  async unmarkAsSuspended(lineId: string): Promise<{ success: boolean; message: string; data: any }> {
    const response = await this.api.put<ApiResponse<{ success: boolean; message: string; data: any }>>(
      `/v1/contract-lines/${lineId}/unmark-suspended`,
      {}
    );
    return this.extractData(response);
  }

  /**
   * Atualiza credenciais do Meu TIM de uma linha
   */
  async updateCredentials(
    lineId: string,
    credentials: UpdateCredentialsDto
  ): Promise<{ success: boolean; message: string; data: any }> {
    const response = await this.api.put<ApiResponse<{ success: boolean; message: string; data: any }>>(
      `/v1/contract-lines/${lineId}/credentials`,
      credentials
    );
    return this.extractData(response);
  }

  /**
   * Obtém credenciais de uma linha específica
   */
  async getLineCredentials(lineId: string): Promise<LineCredentialsDto> {
    const response = await this.api.get<LineCredentialsDto>(
      `/v1/contract-lines/${lineId}/credentials`
    );
    return this.extractData(response);
  }

  /**
   * Obtém resumo completo de uma linha para suspensão
   */
  async getSuspensionSummary(lineId: string): Promise<SuspensionSummaryDto> {
    const response = await this.api.get<SuspensionSummaryDto>(
      `/v1/contract-lines/${lineId}/suspension-summary`
    );
    return this.extractData(response);
  }

  /**
   * Obtém estatísticas de suspensão de linhas
   */
  async getSuspensionStats(
    filters?: {
      dateFrom?: Date;
      dateTo?: Date;
      userId?: string;
    },
    request?: { signal?: AbortSignal }
  ): Promise<SuspensionStatsDto> {
    const params = new URLSearchParams();
    
    if (filters?.dateFrom) {
      params.append('dateFrom', filters.dateFrom.toISOString());
    }
    if (filters?.dateTo) {
      params.append('dateTo', filters.dateTo.toISOString());
    }
    if (filters?.userId) {
      params.append('userId', filters.userId);
    }

    const queryString = params.toString();
    const url = `/v1/contract-lines/suspension-stats${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.api.get<SuspensionStatsDto>(url, { signal: request?.signal });
    return this.extractData(response);
  }

  /**
   * Obtém lista de linhas a enviar (linhas de contratos Fastchip onde SeraUsadaPeloCliente = true)
   */
  async getPendingToSend(filters?: {
    searchTerm?: string;
    dateFrom?: Date;
    dateTo?: Date;
    contractId?: string;
  }): Promise<PendingToSendLineDto[]> {
    const params = new URLSearchParams();
    
    if (filters?.contractId) {
      params.append('contractId', filters.contractId);
    }
    if (filters?.searchTerm) {
      params.append('searchTerm', filters.searchTerm);
    }
    if (filters?.dateFrom) {
      params.append('dateFrom', filters.dateFrom.toISOString());
    }
    if (filters?.dateTo) {
      params.append('dateTo', filters.dateTo.toISOString());
    }

    const queryString = params.toString();
    const url = `/v1/contract-lines/pending-to-send${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.api.get<PendingToSendLineDto[]>(url);
    return this.extractData(response);
  }

  /**
   * Obtém estatísticas de linhas a enviar (métricas de envio)
   */
  async getToSendStats(request?: { signal?: AbortSignal }): Promise<ToSendStatsDto> {
    const response = await this.api.get<ToSendStatsDto>(
      '/v1/contract-lines/to-send-stats',
      { signal: request?.signal }
    );
    return this.extractData(response);
  }

  async getSuspensionsDashboardPage(
    filters: {
    page: number;
    pageSize: number;
    showSuspensions: boolean;
    showToSend: boolean;
    suspensionViewFilter: string;
    searchTerm?: string;
    status?: string;
    contractStatus?: string;
    fastchipEnviado?: boolean;
    temCredenciaisMeuTim?: boolean;
    contractFastchip?: boolean;
    dateFrom?: Date;
    dateTo?: Date;
    periodMode?: SuspensionPeriodMode;
    motivos?: string[];
  },
    request?: { signal?: AbortSignal }
  ): Promise<SuspensionsDashboardPageResponse> {
    const params = new URLSearchParams();
    params.append('page', String(filters.page));
    params.append('pageSize', String(filters.pageSize));
    params.append('showSuspensions', String(filters.showSuspensions));
    params.append('showToSend', String(filters.showToSend));
    params.append('suspensionViewFilter', filters.suspensionViewFilter);
    if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
    if (filters.status) params.append('status', filters.status);
    if (filters.contractStatus) params.append('contractStatus', filters.contractStatus);
    if (filters.fastchipEnviado !== undefined) {
      params.append('fastchipEnviado', String(filters.fastchipEnviado));
    }
    if (filters.temCredenciaisMeuTim !== undefined) {
      params.append('temCredenciaisMeuTim', String(filters.temCredenciaisMeuTim));
    }
    if (filters.contractFastchip !== undefined) {
      params.append('contractFastchip', String(filters.contractFastchip));
    }
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom.toISOString());
    if (filters.dateTo) params.append('dateTo', filters.dateTo.toISOString());
    params.append('periodMode', filters.periodMode ?? 'pendencias');
    filters.motivos?.forEach((m) => params.append('motivos', m));

    const response = await this.api.get<SuspensionsDashboardPageResponse>(
      `/v1/contract-lines/suspensions-dashboard?${params.toString()}`,
      { signal: request?.signal }
    );
    return this.extractData(response);
  }

  /**
   * Gera e envia relatório de suspensões por e-mail
   */
  async generateSuspensionsReport(params: {
    dateFrom: Date;
    dateTo: Date;
    viewMode: string;
    /** T16 — alinhado ao dashboard (T14); default pendências no backend se omitido */
    periodMode?: SuspensionPeriodMode;
    sendEmail?: boolean;
    sendWhatsApp?: boolean;
  }): Promise<void> {
    await this.api.post(
      '/v1/contract-lines/suspensions-report',
      {
        dateFrom: params.dateFrom.toISOString(),
        dateTo: params.dateTo.toISOString(),
        viewMode: params.viewMode,
        periodMode: params.periodMode ?? 'pendencias',
        sendEmail: params.sendEmail ?? true,
        sendWhatsApp: params.sendWhatsApp ?? false,
      }
    );
  }

  async getSuspensionsDashboardLists(filters: {
    searchTerm?: string;
    status?: string;
    contractStatus?: string;
    fastchipEnviado?: boolean;
    temCredenciaisMeuTim?: boolean;
    contractFastchip?: boolean;
    dateFrom?: Date;
    dateTo?: Date;
    periodMode?: SuspensionPeriodMode;
    motivos?: string[];
  },
    request?: { signal?: AbortSignal }
  ): Promise<SuspensionsDashboardListsResponse> {
    const params = new URLSearchParams();
    if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
    if (filters.status) params.append('status', filters.status);
    if (filters.contractStatus) params.append('contractStatus', filters.contractStatus);
    if (filters.fastchipEnviado !== undefined) {
      params.append('fastchipEnviado', String(filters.fastchipEnviado));
    }
    if (filters.temCredenciaisMeuTim !== undefined) {
      params.append('temCredenciaisMeuTim', String(filters.temCredenciaisMeuTim));
    }
    if (filters.contractFastchip !== undefined) {
      params.append('contractFastchip', String(filters.contractFastchip));
    }
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom.toISOString());
    if (filters.dateTo) params.append('dateTo', filters.dateTo.toISOString());
    params.append('periodMode', filters.periodMode ?? 'pendencias');
    filters.motivos?.forEach((m) => params.append('motivos', m));

    const response = await this.api.get<SuspensionsDashboardListsResponse>(
      `/v1/contract-lines/suspensions-dashboard-lists?${params.toString()}`,
      { signal: request?.signal }
    );
    return this.extractData(response);
  }

  async getSuspensionsDueToday(request?: { signal?: AbortSignal }): Promise<SuspensionsDueTodayResponse> {
    const response = await this.api.get<SuspensionsDueTodayResponse>(
      '/v1/contract-lines/suspensions-due-today',
      { signal: request?.signal }
    );
    return this.extractData(response);
  }

  async enqueueSuspensionAutomation(lineIds: string[]): Promise<SuspensionAutomationEnqueueResponse> {
    const response = await this.api.post<SuspensionAutomationEnqueueResponse>(
      '/v1/contract-lines/suspension-automation/enqueue',
      { lineIds }
    );
    return this.extractData(response);
  }

  async retrySuspensionAutomationBatch(batchId: string): Promise<void> {
    await this.api.post(`/v1/contract-lines/suspension-automation/batches/${batchId}/retry`);
  }

  async getSuspensionAutomationHistory(
    query: SuspensionAutomationHistoryQuery,
    request?: { signal?: AbortSignal }
  ): Promise<SuspensionAutomationHistoryResponse> {
    const params = new URLSearchParams();
    if (query.dateFrom) params.append('dateFrom', query.dateFrom);
    if (query.dateTo) params.append('dateTo', query.dateTo);
    if (query.razaoSocial) params.append('razaoSocial', query.razaoSocial);
    if (query.cnpj) params.append('cnpj', query.cnpj);
    if (query.protocolo) params.append('protocolo', query.protocolo);
    if (query.linha) params.append('linha', query.linha);
    params.append('page', String(query.page ?? 1));
    params.append('pageSize', String(query.pageSize ?? 50));

    const response = await this.api.get<SuspensionAutomationHistoryResponse>(
      `/v1/contract-lines/suspension-automation/history?${params.toString()}`,
      { signal: request?.signal }
    );
    return this.extractData(response);
  }

  async clearSuspensionAutomationQueue(
    body: SuspensionAutomationClearQueueRequest
  ): Promise<SuspensionAutomationClearQueueResponse> {
    const response = await this.api.post<SuspensionAutomationClearQueueResponse>(
      '/v1/contract-lines/suspension-automation/clear-queue',
      body
    );
    return this.extractData(response);
  }

  /**
   * Fatia 1 (GCM) — elegibilidade real (gate no servidor: time do contrato + status
   * Ativo/Finalizado + ainda não suspensa + health Meu TIM recente) da ação de 1ª suspensão.
   */
  async getGcmFirstSuspensionEligibility(lineId: string): Promise<GcmFirstSuspensionEligibilityDto> {
    const response = await this.api.get<GcmFirstSuspensionEligibilityDto>(
      `/v1/contract-lines/${lineId}/gcm-first-suspension/eligibility`
    );
    return this.extractData(response);
  }

  /** Fatia 1 (GCM) — enfileira a automação de 1ª suspensão; servidor rejeita se não elegível/health não Ok. */
  async enqueueGcmFirstSuspensionAutomation(lineId: string): Promise<GcmFirstSuspensionActionResultDto> {
    const response = await this.api.post<GcmFirstSuspensionActionResultDto>(
      `/v1/contract-lines/${lineId}/gcm-first-suspension/enqueue-automation`
    );
    return this.extractData(response);
  }

  /** Fatia 1 (GCM) — fallback manual (protocolo opcional) quando a automação não está disponível. */
  async markGcmFirstSuspensionManual(
    lineId: string,
    body: GcmMarkFirstSuspensionRequestDto = {}
  ): Promise<GcmFirstSuspensionActionResultDto> {
    const response = await this.api.post<GcmFirstSuspensionActionResultDto>(
      `/v1/contract-lines/${lineId}/gcm-first-suspension/mark-manual`,
      body
    );
    return this.extractData(response);
  }
}

export interface SuspensionsDueTodayLine {
  lineId: string;
  numero: string | null;
  ddd: string;
  planName: string;
  kind: 'first_suspension' | 'recurring' | string;
  dueDateSp: string | null;
  daysOverdue: number | null;
  isOverdue: boolean;
  temCredenciais: boolean;
  /** E-mail/login do Meu TIM. A listagem não traz a senha. */
  meuTimEmail?: string | null;
  meuTimHealthStatus: string | null;
  warnings: string[];
  hasImpedimento: boolean;
  motivoOperacional: string | null;
  motivoOperacionalLabel: string | null;
  canEnqueue: boolean;
  blockReason: string | null;
  automationStatus: string;
  automationQueuedAt: string | null;
  automationBatchId?: string | null;
  protocolo?: string | null;
  automationErrorCategory?: string | null;
  automationErrorMessage?: string | null;
  automationAttempts?: number;
}

export interface SuspensionsDueTodayContract {
  contractId: string;
  clientId: string;
  contractNumber: string;
  clientName: string;
  clientCnpj: string;
  batchStatus?: string;
  batchId?: string | null;
  batchProtocolo?: string | null;
  batchLastError?: string | null;
  batchErrorCategory?: string | null;
  lines: SuspensionsDueTodayLine[];
}

export interface SuspensionsDueTodaySummary {
  totalLines: number;
  firstSuspension: number;
  recurring: number;
  overdue: number;
  withImpedimento: number;
  missingCredentials: number;
  invalidCredentials: number;
  alreadyQueued: number;
  canEnqueue: number;
  completed: number;
  failed: number;
}

export interface SuspensionsDueTodayResponse {
  referenceDateSp: string;
  summary: SuspensionsDueTodaySummary;
  items: SuspensionsDueTodayContract[];
}

export interface SuspensionAutomationHistoryQuery {
  dateFrom?: string;
  dateTo?: string;
  razaoSocial?: string;
  cnpj?: string;
  protocolo?: string;
  linha?: string;
  page?: number;
  pageSize?: number;
}

export interface SuspensionAutomationHistoryItem {
  jobId: string;
  batchId?: string | null;
  contractId: string;
  clientId: string;
  contractLineId: string;
  clientName: string;
  clientCnpj: string;
  contractNumber: string;
  ddd: string;
  numero?: string | null;
  lineLabel: string;
  status: string;
  protocolo?: string | null;
  errorCategory?: string | null;
  errorMessage?: string | null;
  queuedAt: string;
  completedAt?: string | null;
  dueDateSp?: string | null;
}

export interface SuspensionAutomationHistoryResponse {
  page: number;
  pageSize: number;
  total: number;
  items: SuspensionAutomationHistoryItem[];
}

export interface SuspensionAutomationClearQueueRequest {
  reason?: string;
  batchIds?: string[];
  jobIds?: string[];
}

export interface SuspensionAutomationClearQueueResponse {
  batchesUpdated: number;
  jobsUpdated: number;
  reason: string;
}

export interface SuspensionAutomationEnqueueLineResult {
  lineId: string;
  enqueued: boolean;
  message: string | null;
  warnings: string[];
}

export interface SuspensionAutomationEnqueueContractBatch {
  contractId: string;
  batchId: string;
  lineIds: string[];
  results: SuspensionAutomationEnqueueLineResult[];
}

export interface SuspensionAutomationEnqueueResponse {
  requested: number;
  enqueued: number;
  skipped: number;
  results: SuspensionAutomationEnqueueLineResult[];
  batches?: SuspensionAutomationEnqueueContractBatch[];
}

export interface ToSendStatsDto {
  sentToday: number;
  sentLast7Days: number;
  sentLast30Days: number;
  sentThisMonth: number;
  totalPending: number;
  totalSent: number;
  sentRate: number;
  averageTimeToSend: number;
  /** Agregados pending-to-send (API recente) */
  pendingLinesTotal?: number;
  pendingContractsTotal?: number;
  pendingSedexContracts?: number;
  pendingPacContracts?: number;
  pendingLinesCompleteData?: number;
  pendingLinesIncompleteData?: number;
}

export interface SuspensionsDashboardLineApi {
  id: string;
  type: 'suspension' | 'toSend';
  data: SuspensionLineDto | PendingToSendLineDto;
}

export interface SuspensionsDashboardContractApi {
  contractId: string;
  contractNumber: string;
  clientName: string;
  clientCnpj: string;
  lines: SuspensionsDashboardLineApi[];
}

export interface SuspensionsDashboardPageResponse {
  items: SuspensionsDashboardContractApi[];
  totalContracts: number;
  page: number;
  pageSize: number;
}

export interface SuspensionsDashboardListsResponse {
  suspensions: SuspensionLineDto[];
  linesToSend: PendingToSendLineDto[];
}

export const contractLineService = new ContractLineService();
export default contractLineService;
